import { Row } from 'react-styled-flexboxgrid';
import Section from '@/components/ui/Section';
import breakpoint from 'styled-components-breakpoint';
import styled from 'styled-components';
import { theme } from 'styled-tools';

const ChapterButton = styled.button`
  border: none;
  background: transparent;
  text-align: left;
  padding: 0;
  margin: 0;
  font-size: 1rem;
`;

const AdjustedSection = styled(Section)`
  overflow-x: unset;
`;

const HeadingRow = styled(Row)`
  align-items: center;
  color: ${theme('colors.blue')};
  background-color: #2f3f56;
  min-height: 23rem;
  margin-left: -4rem;
  margin-right: -4rem;
  padding: 0 2.5rem 1rem;

  h1 {
    margin: 0 1rem;
    max-width: 60rem;
  }

  h2 {
    margin-top: 1.3rem;
    margin-left: 2rem;
    color: ${theme('colors.white')};
    font-weight: 500;
  }

  p {
    display: inline;
    background-color: ${theme('colors.white')};
    line-height: 1.3;
    -webkit-box-decoration-break: clone;
    box-decoration-break: clone;
    border-inline-start: 0.2em solid ${theme('colors.white')};
    border-inline-end: 0.2em solid ${theme('colors.white')};
  }

  ${breakpoint('sm', 'md')`
    padding: 4rem 2.5rem 14rem;
    h2{
      margin-left: 1.3rem;
    }
  `}
`;

const Content = styled.div`
  position: relative;
`;

const SubHeadingSection = styled.section`
  position: relative;
  background-color: rgba(47, 63, 86, 0.08);
  padding-top: 5rem;
  padding-bottom: 5rem;
  padding-left: 6.2rem;
  font-size: 1.12rem;
  ${breakpoint('xs', 'sm')`
    padding-left: 0;
  `}
  ${breakpoint('sm', 'md')`
    padding-left: 2.5rem;
    padding-right: 4.5rem;
  `}
`;

const ContentContainer = styled.div`
  padding: 1rem 0 1rem 1rem;
  border-left: 4px solid ${theme('colors.orange')};
  max-width: 44rem;
  p:last-child {
    margin: 0;
  }
`;

const ContentIndexSection = styled.section`
  overflow-x: hidden;
  padding-top: 7.25rem;
  padding-bottom: 6.8rem;

  ${breakpoint('xs', 'sm')`
    padding: 5rem 1.3rem 6.25rem;
  `}

  > div > div:first-child {
    text-align: right;
    ${breakpoint('xs', 'sm')`
      text-align: left;
    `}
  }

  h2 {
    display: inline-block;
    max-width: 400px;
    margin-top: 0;
    padding-right: 3.56rem;

    ${breakpoint('sm', 'md')`
      padding-right: 2.56rem;
    `}
  }

  ol {
    margin: 0;
    counter-reset: item;
    list-style-type: none;
    ${breakpoint('sm', 'md')`
      padding-right: 4rem;
    `}

    li {
      display: flex;
      align-items: baseline;
      h4 {
        margin: 0;
        font-size: 1.5rem;
        cursor: pointer;

        ${breakpoint('md', 'lg')`
          font-size: 1.25rem;
        `}
        ${breakpoint('xs', 'md')`
          font-size: 1rem;
        `}
        :hover {
          color: ${theme('colors.orange')};
        }
      }

      &:before {
        content: counter(item);
        counter-increment: item;
        margin-right: 0.5rem;
        display: flex;
        justify-content: center;
        font-weight: 600;
        color: ${theme('colors.orange')};
        font-size: 1.5rem;
        ${breakpoint('md', 'lg')`
          font-size: 1.25rem;
        `}
        ${breakpoint('xs', 'md')`
          font-size: 1rem;
        `}
      }
      :last-child {
        margin-bottom: 0;
      }
    }
  }
`;

const ChapterSection = styled.section`
  position: relative;
  > div {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  > div > div {
    width: 72.7vw;
    ${breakpoint('md', 'lg')`
      width: 85vw;
    `}
    ${breakpoint('sm', 'md')`
      width: 93vw;
    `}
    ${breakpoint('sm', 'md')`
      width: 93vw;
    `}
    ${breakpoint('xs', 'sm')`
      width: 100vw;
      padding-left: 1rem;
      padding-right: 1rem;
    `}
  }
`;

const ChapterSectionHeader = styled.div`
  background-color: ${theme('colors.blue')};
  padding-top: 5rem;
  padding-bottom: 6.87rem;
  ${breakpoint('xs', 'sm')`
    padding-bottom: 2.5rem;
  `}
`;

const Heading = styled.div`
  h4 {
    color: ${theme('colors.orange')};
    font-size: 1.5rem;
    font-weight: 500;
    text-transform: uppercase;
    margin: 0;
    ${breakpoint('xs', 'md')`
      font-size: 1rem;
    `}
  }

  h2 {
    color: ${theme('colors.white')};
    font-size: 3.5rem;
    margin: 0;
    ${breakpoint('xs', 'md')`
      font-size: 2.5rem;
    `}
  }
`;

const ChapterSectionContent = styled.div`
  position: relative;

  > div {
    padding: 5rem 5rem 5rem;
    background-color: white;
    position: inherit;
    top: -5rem;
    ${breakpoint('sm', 'lg')`
      padding: 2.5rem 2.5rem 5rem;
    `}
    ${breakpoint('xs', 'sm')`
      top: 0;
      padding: 2.5rem 0 8.25rem;
    `}
  }

  figure {
    margin-bottom: 1.66rem;
  }

  div p img {
    display: block;
    margin: 0 auto;
  }

  ${breakpoint('md')`
    font-size: 1.12rem
    h3{
      font-size: 1.56rem
    }
  `}
`;

const FooterSection = styled.section`
  background: linear-gradient(180deg, #fda34a 0%, #f88143 100%);
  color: ${theme('colors.white')};
  padding: 5rem 0 6.5rem 5.5rem;

  > div {
    display: inline-block;
    max-width: 57.2rem;
  }

  h2 {
    margin: 0 0 1.5rem;
    color: inherit;
    font-size: 3.5rem;
    ${breakpoint('xs', 'md')`
      font-size: 2.5rem;
    `}
  }

  ${breakpoint('sm', 'md')`
    padding: 5rem 1.7rem 5rem 1.7rem;
  `}

  ${breakpoint('xs', 'sm')`
    padding: 5rem 1rem 6.25rem 1rem;
    div{
      width: 100%;
    }
  `}
`;

const SocialsContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 100;
  ${breakpoint('xs', 'sm')`
    display: none;
  `}
  div {
    position: absolute;
    top: 2.5rem;
    right: 0;
    background-color: ${theme('colors.blue')};
    width: 4.5rem;
    ul {
      padding: 1.5rem 0;
      text-align: center;
      margin: 0;
      li {
        display: block;
        margin: 0 0 1.75rem;
        :last-child {
          margin: 0;
        }
      }
    }
    svg {
      color: ${theme('colors.white')} !important;
    }

    ${breakpoint('xs', 'sm')`
      top: 1rem;
      width: 3rem;
      ul{
        padding: 0.7rem 0;
        li{
          margin: 0 0 0.6rem;
        }
      }
      svg{
        width: 1.3rem;
        height: 1.3rem;
      }
    `}
  }
`;

export {
  ChapterButton,
  AdjustedSection,
  HeadingRow,
  Content,
  SubHeadingSection,
  ContentContainer,
  ContentIndexSection,
  ChapterSection,
  ChapterSectionHeader,
  Heading,
  ChapterSectionContent,
  FooterSection,
  SocialsContainer,
};
